import React from 'react';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import classnames from 'classnames';
import CardHeader from './CardHeader';
import CardFooter from './CardFooter';
import './Card.scss';
import { DIFFERENTIAL_TYPE, PRICING_TYPE } from '../SkillGeoDifferentialReducer';

class Card extends React.Component {
  static propTypes = {
    count: number,
    icon: string,
    id: string,
    isAdded: bool,
    isExpanded: bool,
    jobFamily: string,
    location: string,
    templateAfterAdd: oneOfType([array, object]),
    templateBeforeAdd: oneOfType([array, object]),
    title: string,
    toggleDifferential: func,
    toggleExpand: func,
    totalAdjustment: number,
    totalAdjustmentOverride: number,
    updateTotalAdjustmentOverride: func,
    differentialLoading: bool,
    differentialFailed: bool,
    isRemoved: bool,
    setRemoved: func,
    isApplied: bool,
    pricingType: string,
    differentialType: string,
    onRemove: func,
    setIsPendingRemove: func,
    isPendingRemove: bool,
    disabled: bool
  };

  constructor(props) {
    super(props);

    this.templateRef = React.createRef();

    this.state = {
      templateHeight: 0
    };

    this.removeCard = this.removeCard.bind(this);
    this.cancelRemove = this.cancelRemove.bind(this);
    this.confirmDiscardChanges = this.confirmDiscardChanges.bind(this);
  }

  componentDidMount() {
    this.setState({ templateHeight: this.getTemplateHeight() });
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.templateRef.current && this.getTemplateHeight() !== this.state.templateHeight) {
      this.setState({ templateHeight: this.getTemplateHeight() });
    }
  }

  render() {
    const {
      count,
      icon,
      id,
      isAdded,
      isExpanded,
      jobFamily,
      location,
      templateAfterAdd,
      templateBeforeAdd,
      title,
      toggleDifferential,
      toggleExpand,
      totalAdjustment,
      totalAdjustmentOverride,
      updateTotalAdjustmentOverride,
      differentialLoading,
      differentialFailed,
      isApplied,
      isPendingRemove,
      disabled
    } = this.props;

    const cardClass = classnames({
      differential__card: true
    });

    const cardPanelClassNames = classnames('card-panel', {
      open: isAdded && isExpanded,
      closed: isAdded && !isExpanded,
      removed: !isAdded
    });

    return (
      <React.Fragment>
        <div className={cardClass}>
          <div className="card-panel__left">
            <CardHeader icon={icon} title={title} onRemove={this.removeCard} isAdded={isAdded} isApplied={isApplied} />
            <div style={{ height: this.state.templateHeight }} className={cardPanelClassNames}>
              <div className="differentials__template" ref={this.templateRef}>
                {isAdded && isExpanded ? templateAfterAdd : templateBeforeAdd}
              </div>
            </div>
          </div>
          {!isAdded && (
            <div className="card-panel__right">
              <div className="card__column action__column" onClick={toggleDifferential}>
                <a className="pxl-btn pxl-btn-default pxl-margin-small add__button">
                  <i className="icon-plus" id={id} /> Add
                </a>
              </div>
            </div>
          )}
          {(differentialLoading || differentialFailed || (isAdded && !!totalAdjustment)) && (
            <CardFooter
              count={count}
              id={id}
              isExpanded={isExpanded}
              totalAdjustment={totalAdjustment}
              totalAdjustmentOverride={totalAdjustmentOverride}
              toggleExpand={toggleExpand}
              jobFamily={jobFamily}
              location={location}
              updateTotalAdjustmentOverride={updateTotalAdjustmentOverride}
              differentialLoading={differentialLoading}
              differentialFailed={differentialFailed}
            />
          )}
          {(isPendingRemove || disabled) && (
            <div className="remove__overlay">
              {isPendingRemove && (
                <React.Fragment>
                  <div className="confirm__message">{this.getRemoveMessage()}</div>
                  <div className="action__buttons">
                    <a className="confirm__cancel pxl-margin-small" onClick={this.cancelRemove}>
                      Cancel
                    </a>
                    <a
                      className="confirm__discard pxl-btn pxl-btn-default pxl-margin-small"
                      onClick={this.confirmDiscardChanges}
                    >
                      {this.getRemoveButtonLabel()}
                    </a>
                  </div>
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  getTemplateHeight() {
    const { isAdded, isExpanded } = this.props;
    if (this.templateRef.current) {
      if (isAdded && !isExpanded) {
        return 0;
      }
      return this.templateRef.current.offsetHeight;
    }
    return 0;
  }

  removeCard() {
    const { setIsPendingRemove } = this.props;
    setIsPendingRemove(true);
  }

  cancelRemove() {
    const { setIsPendingRemove } = this.props;
    setIsPendingRemove(false);
  }

  confirmDiscardChanges() {
    const { toggleDifferential, onRemove, setRemoved } = this.props;
    toggleDifferential();
    setRemoved(true).then(() => {
      onRemove();
    });
  }

  getRemoveMessage() {
    const { differentialType, pricingType } = this.props;
    const removeDifferentialText = differentialType === DIFFERENTIAL_TYPE.GEO ? 'geo' : 'skill';
    const jobMatchText = pricingType === PRICING_TYPE.SLOTTED ? 'slotted' : 'survey';
    return `Removing ${removeDifferentialText} differential will reset ${removeDifferentialText} 
      adjustment to 1 for all applied ${jobMatchText} job matches. Are you sure you want to proceed?`;
  }

  getRemoveButtonLabel() {
    const { differentialType } = this.props;
    return `Remove ${differentialType === DIFFERENTIAL_TYPE.GEO ? 'Geo' : 'Skill'} Differential`;
  }
}

export default Card;
